import { useMutation, useApolloClient } from "@apollo/client";
import { useEffect, useState, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-hot-toast";
import { UserGroupIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  AddUserGroupMembersDocument,
  CreateUserGroupDocument,
  DeleteUserGroupDocument,
  EventDetailsDocument,
  ProjectDetailsDocument,
  PublicUserOrganization,
  RemoveUserGroupMembersDocument,
  UpdateUserGroupDocument,
  UserGroupFragment,
  UserGroupsDocument,
} from "../../../../../../../../__generated__/graphql";
import Button from "../../../../../../../../components/Button";
import FormInput from "../../../../../../../../components/FormInput";
import Modal from "../../../../../../../../components/Modal";
import SelectedMembersDisplay from "../../../../../../../../components/SelectedMembersDisplay";
import ConfirmDialog from "../../../../../../../../components/ConfirmDialog";
import ParticipantsSelectionModal from "../../../../../../../../components/ParticipantsSelectionModal";
import {
  userGroupSchema,
  UserGroupFormValues,
  defaultValues,
} from "./zod/userGroupSchema";

interface UserGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
  eventId?: string;
  userGroup?: UserGroupFragment | null;
  onGroupCreated?: (group: UserGroupFragment) => void;
  onGroupUpdated?: () => void;
  readOnly?: boolean;
}

export default function UserGroupModal({
  isOpen,
  onClose,
  projectId,
  eventId,
  userGroup,
  onGroupCreated,
  onGroupUpdated,
  readOnly = false,
}: UserGroupModalProps) {
  const isEditing = !!userGroup;

  const [isSelectingMembers, setIsSelectingMembers] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<
    PublicUserOrganization[]
  >([]);
  const wasOpenRef = useRef(false);
  const apolloClient = useApolloClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UserGroupFormValues>({
    resolver: zodResolver(userGroupSchema),
    defaultValues: {
      name: userGroup?.name ?? defaultValues.name,
      description: userGroup?.description ?? defaultValues.description,
    },
  });

  // Only reset form and members when modal opens (transitions from closed to open)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Modal just opened
      reset({
        name: userGroup?.name ?? defaultValues.name,
        description: userGroup?.description ?? defaultValues.description,
      });
      setSelectedMembers(
        userGroup?.members.map((m) => m.publicUserOrganization) ?? []
      );
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, userGroup, reset]);

  // Determine the projectId and eventId to use for refetching (from prop or from editing group)
  const effectiveProjectId = projectId || userGroup?.projectId;
  const effectiveEventId = eventId || userGroup?.eventId;

  // Build refetch queries - always refetch UserGroups, and ProjectDetails/EventDetails if we have IDs
  const refetchQueries = useMemo(() => {
    const queries: Array<
      | { query: typeof UserGroupsDocument }
      | { query: typeof ProjectDetailsDocument; variables: { projectId: string } }
      | { query: typeof EventDetailsDocument; variables: { eventId: string } }
    > = [{ query: UserGroupsDocument }];
    if (effectiveProjectId) {
      queries.push({
        query: ProjectDetailsDocument,
        variables: { projectId: effectiveProjectId },
      });
    }
    if (effectiveEventId) {
      queries.push({
        query: EventDetailsDocument,
        variables: { eventId: effectiveEventId },
      });
    }
    return queries;
  }, [effectiveProjectId, effectiveEventId]);

  const [createUserGroup, { loading: isCreating }] = useMutation(
    CreateUserGroupDocument,
    {
      refetchQueries,
    }
  );

  const [updateUserGroup, { loading: isUpdating }] = useMutation(
    UpdateUserGroupDocument,
    {
      refetchQueries,
    }
  );

  const [addUserGroupMembers, { loading: isAddingMembers }] = useMutation(
    AddUserGroupMembersDocument,
    {
      refetchQueries,
    }
  );

  const [removeUserGroupMembers, { loading: isRemovingMembers }] = useMutation(
    RemoveUserGroupMembersDocument,
    {
      refetchQueries,
    }
  );

  const [deleteUserGroup, { loading: isDeleting }] = useMutation(
    DeleteUserGroupDocument,
    {
      refetchQueries,
    }
  );

  const handleMembersSelected = (members: PublicUserOrganization[]) => {
    setSelectedMembers(members);
  };

  const removeMember = (memberId: string) => {
    setSelectedMembers(selectedMembers.filter((m) => m.id !== memberId));
  };

  const onSubmit = async (data: UserGroupFormValues) => {
    try {
      if (isEditing && userGroup) {
        // Update group details
        await updateUserGroup({
          variables: {
            updateUserGroupInput: {
              id: userGroup.id,
              name: data.name,
              description: data.description || undefined,
            },
          },
        });

        // Handle member changes
        const currentMemberIds = userGroup.members.map(
          (m) => m.publicUserOrganization.id
        );
        const newMemberIds = selectedMembers.map((m) => m.id);

        const membersToAdd = newMemberIds.filter(
          (id) => !currentMemberIds.includes(id)
        );
        const membersToRemove = currentMemberIds.filter(
          (id) => !newMemberIds.includes(id)
        );

        if (membersToAdd.length > 0) {
          await addUserGroupMembers({
            variables: {
              addUserGroupMembersInput: {
                userGroupId: userGroup.id,
                userOrganizationIds: membersToAdd,
              },
            },
          });
        }

        if (membersToRemove.length > 0) {
          await removeUserGroupMembers({
            variables: {
              removeUserGroupMembersInput: {
                userGroupId: userGroup.id,
                userOrganizationIds: membersToRemove,
              },
            },
          });
        }

        // Refetch project/event details to update participants list
        // Use refetchQueries to properly trigger React re-renders for watched queries
        await apolloClient.refetchQueries({
          include: [UserGroupsDocument, ProjectDetailsDocument, EventDetailsDocument],
        });

        // Notify parent component that the group was updated
        onGroupUpdated?.();

        toast.success("Groupe modifié avec succès");
      } else {
        // Create new group
        const result = await createUserGroup({
          variables: {
            createUserGroupInput: {
              name: data.name,
              description: data.description || undefined,
              projectId,
              eventId,
              memberUserOrganizationIds: selectedMembers.map((m) => m.id),
            },
          },
        });
        
        const createdGroup = result.data?.createUserGroup;
        if (createdGroup) {
          onGroupCreated?.(createdGroup);
        }
        
        toast.success("Groupe créé avec succès");
      }

      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Une erreur est survenue"
      );
    }
  };

  const handleDeleteClick = () => {
    setShowConfirmDelete(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userGroup) return;

    try {
      await deleteUserGroup({
        variables: {
          userGroupId: userGroup.id,
        },
      });
      toast.success("Groupe supprimé avec succès");
      setShowConfirmDelete(false);
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Une erreur est survenue"
      );
    }
  };

  const isLoading =
    isCreating || isUpdating || isAddingMembers || isRemovingMembers || isDeleting;

  return (
    <>
      <ParticipantsSelectionModal
        isOpen={isSelectingMembers}
        onClose={() => setIsSelectingMembers(false)}
        onSubmit={handleMembersSelected}
        title="Sélectionner les membres du groupe"
        description="Choisissez les participants qui feront partie de ce groupe."
        initialSelectedUsers={selectedMembers}
        submitButtonText="Valider la sélection"
      />

      {isEditing && userGroup && (
        <ConfirmDialog
          isOpen={showConfirmDelete}
          onClose={() => setShowConfirmDelete(false)}
          onConfirm={handleDeleteConfirm}
          title="Supprimer le groupe"
          description={`Êtes-vous sûr de vouloir supprimer le groupe "${userGroup.name}" ? Cette action est irréversible.`}
          confirmText="Supprimer"
          cancelText="Annuler"
          isLoading={isDeleting}
          variant="danger"
        />
      )}

      <Modal
        dataTestId="group-modal"
        isModalOpen={isOpen && !isSelectingMembers}
        onClose={onClose}
        title={
          readOnly
            ? "Détails du groupe"
            : isEditing
            ? "Modifier le groupe"
            : "Créer un groupe"
        }
        description={
          readOnly
            ? undefined
            : isEditing
            ? "Modifiez les informations du groupe"
            : "Créez un nouveau groupe de participants"
        }
        className="w-xl"
        dialogClassName="z-[300]"
      >
        {readOnly ? (
          <div className="flex flex-col gap-4">
            <div>
              <div className="text-sm font-medium text-secondary mb-1">
                Nom du groupe
              </div>
              <div className="text-primary">{userGroup?.name}</div>
            </div>

            {userGroup?.description && (
              <div>
                <div className="text-sm font-medium text-secondary mb-1">
                  Description
                </div>
                <div className="text-primary whitespace-pre-wrap">
                  {userGroup.description}
                </div>
              </div>
            )}

            <div>
              <div className="text-sm font-medium text-secondary mb-2">
                Membres du groupe ({selectedMembers.length})
              </div>
              {selectedMembers.length === 0 ? (
                <div className="text-sm text-tertiary">Aucun membre</div>
              ) : (
                <SelectedMembersDisplay members={selectedMembers} />
              )}
            </div>

            <div className="flex justify-end pt-4">
              <Button variant="secondary" onClick={onClose}>
                Fermer
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <FormInput
              dataTestId="group-name-input"
              id="name"
              label="Nom du groupe"
              register={register}
              error={errors.name}
              placeholder="Ex: Équipe technique"
            />

            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium mb-1"
              >
                Description{" "}
                <span className="text-sm text-tertiary">(optionnel)</span>
              </label>
              <textarea
                {...register("description")}
                data-testid="group-description-input"
                id="description"
                className="w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border placeholder:text-tertiary min-h-[80px] resize-none"
                placeholder="Description du groupe..."
              />
            </div>

            {/* Selected Members Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">
                  Membres du groupe ({selectedMembers.length})
                </label>
                <Button
                  dataTestId="group-members-button"
                  type="button"
                  variant="secondary"
                  className="gap-1 text-sm"
                  onClick={() => setIsSelectingMembers(true)}
                >
                  <PlusIcon className="w-4 h-4" />
                  {selectedMembers.length > 0
                    ? "Modifier les membres"
                    : "Ajouter des membres"}
                </Button>
              </div>

              {selectedMembers.length === 0 ? (
                <div
                  className="border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-accent/50 hover:bg-accent/5 transition-colors"
                  onClick={() => setIsSelectingMembers(true)}
                >
                  <UserGroupIcon className="w-8 h-8 text-tertiary" />
                  <div className="text-sm text-tertiary text-center">
                    Cliquez pour ajouter des membres au groupe
                  </div>
                </div>
              ) : (
                <SelectedMembersDisplay
                  members={selectedMembers}
                  onRemove={removeMember}
                />
              )}
            </div>

            <div className="flex justify-between items-center pt-4">
              {isEditing ? (
                <Button
                  dataTestId="group-delete-button"
                  variant="danger"
                  type="button"
                  onClick={handleDeleteClick}
                  disabled={isLoading}
                  className="gap-1"
                >
                  <TrashIcon className="w-4 h-4" />
                  Supprimer
                </Button>
              ) : (
                <div />
              )}
              <div className="flex gap-2">
                <Button dataTestId="group-cancel-button" variant="secondary" type="button" onClick={onClose}>
                  Annuler
                </Button>
                <Button dataTestId="group-submit-button" type="submit" isLoading={isLoading} disabled={isLoading}>
                  {isEditing ? "Enregistrer" : "Créer le groupe"}
                </Button>
              </div>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
